import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import axiosInstance from "../utils/axiosInstance";
import { setUserData } from "../store/userSlice";
import { ensureKeypair } from "../crypto/cryptoKeys";
import { broadcastSessionChange, getHomePathForRole } from "../utils/sessionSync";
import { invalidateUserFetches, refreshSessionData } from "../utils/refreshSessionData";
import "../assets/css/style.css";
import "../assets/bp/BPEXCH-style.css";

const Register = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const ref = searchParams.get("ref") || "";
  const [codeStatus, setCodeStatus] = useState("checking");
  const [referrerName, setReferrerName] = useState("");

  useEffect(() => {
    if (!ref) {
      setCodeStatus("invalid");
      return;
    }

    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/auth/check-affiliate-code`, {
        params: { ref }
      })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.status) {
          setReferrerName(res.data.data?.name || "");
          setCodeStatus("valid");
        } else {
          setCodeStatus("invalid");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setCodeStatus(error?.response?.status === 404 ? "invalid" : "error");
      });

    return () => {
      cancelled = true;
    };
  }, [ref]);

  // All hooks must be declared unconditionally — rendering decides what to show
  const [uname, setUname] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobno, setMobno] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);
  const submittingRef = useRef(false);

  const validate = () => {
    if (!uname.trim()) return "Username is required";
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(uname.trim()))
      return "Username must be 3–20 characters (letters, numbers, underscore)";
    if (!password) return "Password is required";
    if (password.length < 4) return "Password must be at least 4 characters";
    if (password !== confirmPassword) return "Passwords do not match";
    return null;
  };

  const unlockSubmit = () => {
    submittingRef.current = false;
    setSubmitting(false);
    setSigningIn(false);
  };

  const autoLogin = async () => {
    localStorage.removeItem("popupShown");
    const { publicJwk } = await ensureKeypair();
    await axiosInstance.post("crypto/register-key", { publicJwk });
    const loginRes = await axiosInstance.post(
      `${process.env.REACT_APP_API_URL}/api/auth/login`,
      { uname: uname.trim(), passpin: password, publicJwk },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      }
    );
    const user = loginRes.data.user;
    invalidateUserFetches();
    await dispatch(setUserData(user));
    broadcastSessionChange(user);
    await refreshSessionData(dispatch);
    navigate(getHomePathForRole(user.user_role), { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setServerError("");

    const err = validate();
    if (err) {
      setServerError(err);
      unlockSubmit();
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/register`,
        {
          ref,
          uname: uname.trim(),
          password,
          mobno: mobno.trim(),
          reference: reference.trim(),
          note: note.trim(),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (!res.data?.status) {
        setServerError(res.data?.message || "Registration failed. Please try again.");
        unlockSubmit();
        return;
      }

      setSigningIn(true);
      try {
        await autoLogin();
      } catch {
        setSuccess(true);
      }
    } catch (error) {
      if (!error?.response) {
        setServerError("Cannot reach the server. Please try again in a moment.");
      } else {
        setServerError(
          error?.response?.data?.message || "Registration failed. Please try again."
        );
      }
      unlockSubmit();
    }
  };

  if (codeStatus === "checking") {
    return (
      <div
        style={{
          backgroundImage: "url('/assets/images/Login-bg.jpg')",
          backgroundSize: "cover",
          minHeight: "100vh"
        }}
      />
    );
  }

  if (codeStatus === "invalid") {
    return (
      <div
        style={{
          backgroundImage: "url('/assets/images/Login-bg.jpg')",
          backgroundSize: "cover",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div
          style={{
            background: "rgba(0,0,0,0.45)",
            borderRadius: "12px",
            padding: "40px",
            textAlign: "center",
            color: "#fff"
          }}
        >
          <h4>Page not found</h4>
          <p style={{ fontSize: "14px", marginTop: "8px", opacity: 0.8 }}>
            The page you are looking for does not exist.
          </p>
          <Link to="/" style={{ color: "#00b181" }}>
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundImage: "url('/assets/images/Login-bg.jpg')",
        backgroundSize: "cover",
        minHeight: "100vh",
      }}
    >
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        textarea:-webkit-autofill {
          -webkit-text-fill-color: #fff;
          -webkit-box-shadow: 0 0 0px 1000px transparent inset;
          transition: background-color 5000s ease-in-out 0s;
        }
        .reg-input, .reg-textarea {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 2px solid rgba(255,255,255,0.4);
          outline: none;
          color: #fff;
          font-size: 15px;
          padding: 10px 0 8px 0;
        }
        .reg-textarea {
          resize: vertical;
          min-height: 60px;
        }
        .reg-input::placeholder,
        .reg-textarea::placeholder { color: rgba(255,255,255,0.6); }
        .reg-input:focus,
        .reg-textarea:focus { border-bottom-color: #00b181; }
        .reg-field { margin-bottom: 22px; }
        .reg-label {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(255,255,255,0.55);
          margin-bottom: 2px;
        }
        .reg-btn {
          width: 100%;
          padding: 12px;
          background: #00b181;
          border: none;
          border-radius: 6px;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 8px;
          transition: opacity 0.2s;
        }
        .reg-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .reg-btn:hover:not(:disabled) { opacity: 0.9; }
      `}</style>

      <section className="login-section-wrapper">
        <div className="container">
          <div className="row">
            <div className="login-section-row">
              <div className="wrap-login100">

                <span
                  className="login100-form-logo"
                  style={{ paddingBottom: "13px", backgroundColor: "transparent" }}
                >
                  <img
                    src={process.env.REACT_APP_LOGO_URL || "/assets/images/b2.png"}
                    alt="Logo"
                    style={{ width: "120px", height: "120px", borderRadius: "60px" }}
                  />
                </span>

                {success ? (
                  <div style={{ textAlign: "center", color: "#fff" }}>
                    <div style={{ fontSize: "48px", marginBottom: "12px" }}>✓</div>
                    <h5 style={{ marginBottom: "8px" }}>Registration Successful!</h5>
                    <p style={{ fontSize: "14px", opacity: 0.8, marginBottom: "20px" }}>
                      Your account has been created, but we could not sign you in automatically. Please log in.
                    </p>
                    <button className="reg-btn" onClick={() => navigate("/")}>
                      Go to Login
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="login-form login-form-mobile-views"
                    noValidate
                  >
                    <div
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "13px",
                        marginBottom: "18px",
                        textAlign: "center",
                      }}
                    >
                      Referred by:{" "}
                      <strong style={{ color: "#00b181" }}>{referrerName}</strong>
                    </div>

                    <div className="reg-field">
                      <label className="reg-label">Username *</label>
                      <input
                        className="reg-input"
                        type="text"
                        placeholder="Choose a username"
                        value={uname}
                        onChange={(e) => setUname(e.target.value)}
                        autoComplete="username"
                        maxLength={20}
                      />
                    </div>

                    <div className="reg-field">
                      <label className="reg-label">Password *</label>
                      <input
                        className="reg-input"
                        type="password"
                        placeholder="Create a password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </div>

                    <div className="reg-field">
                      <label className="reg-label">Confirm Password *</label>
                      <input
                        className="reg-input"
                        type="password"
                        placeholder="Repeat password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </div>

                    {serverError && (
                      <div
                        style={{
                          color: "#ff6b6b",
                          fontSize: "13px",
                          marginBottom: "12px",
                          textAlign: "center",
                        }}
                      >
                        {serverError}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="reg-btn"
                      disabled={submitting}
                    >
                      {signingIn
                        ? "Signing in..."
                        : submitting
                          ? "Registering..."
                          : "Create Account"}
                    </button>

                    <div
                      style={{
                        textAlign: "center",
                        marginTop: "16px",
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      Already have an account?{" "}
                      <Link to="/" style={{ color: "#00b181" }}>
                        Login
                      </Link>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Register;
